import React, { useState, useMemo, useEffect } from 'react';
import {
  Autocomplete,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import customAxios from 'routes/Pages/util/Api';
import authHeader from 'services/auth/jwt/auth-header';

export default function ProductsManager({ subCompartmentId, createdAssessmentId, subCompartmentData }) {
  const [products, setProducts] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [destinationOptions, setDestinationOptions] = useState([]);
  const [sageCustomers, setSageCustomers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [form, setForm] = useState({
    products: '',
    productsPercent: '',
    productsDestination: '',
    customerCode: '',
    customerName: '',
  });
  const [error, setError] = useState('');

  // Fetch dropdown data and existing products
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch dropdown options
        const [productsRes, destinationsRes, sageCustomersRes] = await Promise.all([
          customAxios.get('/Ops/GetProducts', { headers: authHeader() }),
          customAxios.get('/CRM/GetAllCustomerLocations', { headers: authHeader() }),
          customAxios.get('/CRM/GetSageCustomers', { headers: authHeader() }),
        ]);

        if (productsRes.status === 200) {
          setProductOptions(productsRes.data.map(p => p.productName));
        }

        if (destinationsRes.status === 200) {
          setDestinationOptions(destinationsRes.data.map(d => d.destinationName));
        }

        if (sageCustomersRes.status === 200) {
          setSageCustomers(sageCustomersRes.data || []);
        }

        // Fetch existing products for this assessment
        if (createdAssessmentId) {
          const productsResponse = await customAxios.get(`/SiteDetails/GetSiteOptions/${createdAssessmentId}`, {
            headers: authHeader(),
          });
          if (productsResponse.status === 200) {
            setProducts(productsResponse.data.products || []);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [subCompartmentId, createdAssessmentId]);

  const totalPercentage = useMemo(() => products.reduce((sum, p) => sum + Number(p.productsPercent || 0), 0), [products]);

  const openAddDialog = () => {
    setEditingProduct(null);
    setForm({ products: '', productsPercent: '', productsDestination: '', customerCode: '', customerName: '' });
    setError('');
    setDialogOpen(true);
  };

  const openEditDialog = product => {
    setEditingProduct(product);
    setForm({
      products: product.products,
      productsPercent: product.productsPercent,
      productsDestination: product.productsDestination || '',
      customerCode: product.customerCode || '',
      customerName: product.customerName || '',
    });
    setError('');
    setDialogOpen(true);
  };

  const handleDelete = async product => {
    try {
      if (product.id) {
        const payload = {
          siteAssessmentID: createdAssessmentId,
          id: product.id,
        };
        await customAxios.post('/SiteDetails/DeleteSiteDetailsProducts', payload, {
          headers: authHeader(),
        });
      }
      // Update local state
      setProducts(prev => prev.filter(p => p.id !== product.id));
    } catch (error) {
      console.error('Error deleting product:', error);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setError('');
  };

  const handleFormChange = e => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSave = async () => {
    // Validation
    if (!form.products) {
      setError('Product is required');
      return;
    }

    const percent = Number(form.productsPercent);
    if (isNaN(percent) || percent < 0 || percent > 100) {
      setError('Percentage must be between 0 and 100');
      return;
    }

    // Calculate total percentage excluding current product being edited
    const otherProductsTotal = products
      .filter(p => !editingProduct || p.id !== editingProduct.id)
      .reduce((sum, p) => sum + Number(p.productsPercent), 0);

    if (otherProductsTotal + percent > 100) {
      setError(`Total cannot exceed 100%. Current total: ${otherProductsTotal}%`);
      return;
    }

    // For adding new product, check if already exists
    if (!editingProduct && products.some(p => p.products === form.products)) {
      setError('This product is already added');
      return;
    }

    try {
      const productData = {
        siteAssessmentID: createdAssessmentId,
        products: form.products,
        productsLen: '',
        productsMinDia: '',
        productsPercent: percent,
        productsDestination: form.productsDestination || '',
        productsPricePerTonne: 0,
        price: 0,
        subCompartmentName: subCompartmentData?.mapLabel || '',
        customerCode: form.customerCode || '',
        customerName: form.customerName || '',
      };

      if (editingProduct) {
        // Edit API call
        const editPayload = {
          ...productData,
          id: editingProduct.id,
        };

        await customAxios.post('/SiteDetails/EditSiteDetailsProducts', editPayload, { headers: authHeader() });
      } else {
        // Add API call - backend expects array
        const addPayload = [productData];

        await customAxios.post('/SiteDetails/AddSiteDetailsProducts', addPayload, { headers: authHeader() });
      }

      // Refresh products from API after save
      const productsResponse = await customAxios.get(`/SiteDetails/GetSiteOptions/${createdAssessmentId}`, {
        headers: authHeader(),
      });
      if (productsResponse.status === 200) {
        setProducts(productsResponse.data.products || []);
      }

      setDialogOpen(false);
      setError('');
    } catch (error) {
      console.error('Error saving product:', error);
      setError(error.response?.data?.message || 'Failed to save product');
    }
  };

  return (
    <Box p={2}>
      <Alert severity="info" sx={{ mb: 2 }}>
        Total percentage cannot exceed 100%. Current total: {totalPercentage}%
      </Alert>

      <Button variant="contained" color="primary" onClick={openAddDialog} sx={{ mb: 2 }}>
        Add Product
      </Button>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Product</TableCell>
            <TableCell align="right">%</TableCell>
            <TableCell>Destination</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {products.map(product => (
            <TableRow key={product.id}>
              <TableCell>{product.products}</TableCell>
              <TableCell align="right">{product.productsPercent}%</TableCell>
              <TableCell>{product.productsDestination || '-'}</TableCell>
              <TableCell>{product.customerName || '-'}</TableCell>
              <TableCell align="right">
                <IconButton size="small" onClick={() => openEditDialog(product)}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" onClick={() => handleDelete(product)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Dialog for Add/Edit */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="xs" fullWidth>
        <DialogTitle>{editingProduct ? 'Edit Product' : 'Add Product'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField select name="products" label="Product" value={form.products} onChange={handleFormChange} fullWidth>
              <MenuItem value="">Select Product</MenuItem>
              {productOptions.map(option => (
                <MenuItem
                  key={option}
                  value={option}
                  disabled={products.some(p => p.products === option && (!editingProduct || p.id !== editingProduct.id))}>
                  {option}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              type="number"
              name="productsPercent"
              label="Percentage"
              value={form.productsPercent}
              onChange={handleFormChange}
              inputProps={{ min: 0, max: 100, step: 0.1 }}
              fullWidth
            />

            <TextField
              select
              name="productsDestination"
              label="Destination"
              value={form.productsDestination}
              onChange={handleFormChange}
              fullWidth>
              <MenuItem value="">Select Destination</MenuItem>
              {destinationOptions.map(option => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>

            <Autocomplete
              options={sageCustomers}
              getOptionLabel={option => `${option.customerName} (${option.customerCode})`}
              value={sageCustomers.find(c => c.customerCode === form.customerCode) || null}
              onChange={(_, selected) => {
                setForm(prev => ({
                  ...prev,
                  customerCode: selected?.customerCode || '',
                  customerName: selected?.customerName || '',
                }));
              }}
              renderInput={params => <TextField {...params} label="Customer" fullWidth />}
              isOptionEqualToValue={(option, value) => option.customerCode === value.customerCode}
            />

            {error && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {error}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            {editingProduct ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
